/**
 * Virtual gamepad overlay for mobile — two nipplejs joysticks + ABXY buttons.
 * Feeds a fake W3C Gamepad into the GameLift Streams SDK.
 */

import { useCallback, useEffect, useRef } from "react";
import type { GameLiftStreams } from "../lib/gamelift-streams-websdk/gameliftstreams-1.1.0.mjs";
import { LuMic, LuMousePointerClick } from "react-icons/lu";
import { VirtualJoystick } from "./VirtualJoystick";
import { VirtualGamepadButton } from "./VirtualGamepadButton";

// Standard gamepad button indices (Xbox layout)
const BTN_A = 0;
const BTN_B = 1;
const BTN_X = 2;
const BTN_Y = 3;
const BTN_RB = 5;

// Axes: 0=LX, 1=LY, 2=RX, 3=RY
const AXIS_LX = 0;
const AXIS_LY = 1;
const AXIS_RX = 2;
const AXIS_RY = 3;

const NUM_BUTTONS = 17; // standard gamepad has 17 buttons
const NUM_AXES = 4;

/** Single mutable gamepad object — GLS tracks it by reference and diffs between processGamepads() calls */
function createMutableGamepad() {
  const btns: GamepadButton[] = Array.from({ length: NUM_BUTTONS }, () => ({
    pressed: false, touched: false, value: 0,
  }));
  return {
    id: "VirtualGamepad",
    index: 100, // 100+ avoids conflict with real gamepads
    connected: true,
    timestamp: performance.now(),
    mapping: "standard" as GamepadMappingType,
    axes: [0, 0, 0, 0],
    buttons: btns,
    hapticActuators: [] as GamepadHapticActuator[],
    vibrationActuator: null as GamepadHapticActuator | null,
  }; // we cast to Gamepad when passing to glsInstance.addGamepad()
}

interface VirtualGamepadProps {
  glsInstance: GameLiftStreams;
}

export function VirtualGamepad({ glsInstance }: VirtualGamepadProps) {
  const gamepadRef = useRef<ReturnType<typeof createMutableGamepad> | null>(null);
  const requestAnimationId = useRef(0);

  // Function for resetting gamepad state to the defaults. Used for restoring inputs for GLS after window lost focus
  const resetGamepadState = useCallback(() => {
    const gamepad = gamepadRef.current;
    if (!gamepad) return;

    for (let i = 0; i < gamepad.axes.length; i++) {
      gamepad.axes[i] = 0;
    }
    for (let i = 0; i < gamepad.buttons.length; i++) {
      const btn = gamepad.buttons[i] as { pressed: boolean; touched: boolean; value: number };
      btn.pressed = false;
      btn.touched = false;
      btn.value = 0;
    }
    (gamepad as any).timestamp = performance.now();
  }, []);

  // Initialise the virtual GameLift gamepad object whenever glsInstance changes
  useEffect(() => {
    const gamepad = createMutableGamepad();
    gamepadRef.current = gamepad;

    // Try to attach the gamepad to the glsIstance
    let added = false;
    added = glsInstance.addGamepad(gamepad as Gamepad);
    console.log("[VGamepad] addGamepad result:", added);

    // Diagnose GLS internal state
    console.log("[VGamepad] glsInstance identity:", glsInstance);
    console.log("[VGamepad] gamepad object:", JSON.stringify({
      id: gamepad.id, index: gamepad.index, connected: gamepad.connected,
      mapping: gamepad.mapping, axesLen: gamepad.axes.length, buttonsLen: gamepad.buttons.length,
      timestamp: gamepad.timestamp,
    }));


    let frameCount = 0;
    let lastLogTime = 0;

    // Process inputs on virtual gamepad on every animation frame
    const tick = () => {
      // Retry add if it failed initially (stream wasn't ready yet)
      if (!added) {
        added = glsInstance.addGamepad(gamepad as Gamepad);
        if (added) console.log("[VGamepad] addGamepad succeeded on retry (frame " + frameCount + ")");
      }
      
      (gamepad as any).timestamp = performance.now(); // Update timestamp so GLS knows the state is fresh

      const processOk = glsInstance.processGamepads();

      frameCount++;
      const now = performance.now();
      const hasInput = gamepad.axes.some((a: number) => Math.abs(a) > 0.01) || gamepad.buttons.some((b: GamepadButton) => b.pressed);
      if (hasInput && now - lastLogTime > 200) {
        console.log(
          "[VGamepad] INPUT active — axes:", [...gamepad.axes], 
          "pressedBtns:", gamepad.buttons.reduce((acc: number[], b: GamepadButton, i: number) => b.pressed ? [...acc, i] : acc, []), "| process:", processOk
        );
        lastLogTime = now;
      } else if (now - lastLogTime > 2000) {
        console.log("[VGamepad] tick #" + frameCount, "| added:", added, "process:", processOk, "| axes:", [...gamepad.axes]);
        lastLogTime = now;
      }

      requestAnimationId.current = requestAnimationFrame(tick);
    };
    requestAnimationId.current = requestAnimationFrame(tick);

    // Restore inputs when the tab's focus changes (to correctly send inputs to GLS when after focus is lost)
    const handleInputRestore = () => {
      if (document.visibilityState !== "visible") return; // Only restore when the tab is visible to avoid processing stale state
      resetGamepadState();
      glsInstance.attachInput();
      glsInstance.processGamepads();
      console.log("[VGamepad] Input restored after focus/visibility change");
    };
    window.addEventListener("focus", handleInputRestore);
    document.addEventListener("visibilitychange", handleInputRestore);

    return () => {
      console.log("[VGamepad] Cleanup — removing gamepad");
      cancelAnimationFrame(requestAnimationId.current);
      window.removeEventListener("focus", handleInputRestore);
      document.removeEventListener("visibilitychange", handleInputRestore);
      glsInstance.removeGamepad(gamepad as Gamepad);
    };
  }, [glsInstance, resetGamepadState]);

  // Callbacks for virtual joysticks
  const leftJoystickCallback = useCallback((x: number, y:number) => {
    if (gamepadRef.current) {
      gamepadRef.current.axes[AXIS_LX] = x;
      gamepadRef.current.axes[AXIS_LY] = y;
    }
  }, [gamepadRef.current])

  const rightJoystickCallback = useCallback((x: number, y:number) => {
    if (gamepadRef.current) {
      gamepadRef.current.axes[AXIS_RX] = x / 2;
      gamepadRef.current.axes[AXIS_RY] = y / 2;
    }
  }, [gamepadRef.current])

  // Callbacks for virtual gamepad buttons to set gls gamepad state
  const setButtonState = useCallback((idx: number, isPressed: boolean) => {
    const gamepad = gamepadRef.current
    if (!gamepad) return
      const btn = gamepad.buttons[idx] as { pressed: boolean; touched: boolean; value: number }
      btn.pressed = isPressed
      btn.touched = isPressed
      btn.value = isPressed ? 1 : 0
  }, [gamepadRef.current])


  return (
    <div className="virtual-gamepad-overlay">
      <div className="virtual-gamepad-container">

        {/* Left joystick */}
        <div className="joystick-left">
          <VirtualJoystick 
            name="Left (Move)" 
            setParentXY={leftJoystickCallback}
            size={125}
          />
        </div>
      
        {/* Right joystick */}
        <div className="joystick-right"> 
          <VirtualJoystick 
            name="Right (Camera)" 
            setParentXY={rightJoystickCallback}
            size={125}
          />
        </div>

        <div className="gamepad-btn-zone-left">
          {/* Virtual Interact button */}
          <div className="gamepad-btn-interact">
            <VirtualGamepadButton 
              label={<LuMousePointerClick className="gamepad-btn-icon" />} 
              onDownHandler={() => { 
                setButtonState(BTN_X, true)
              }} 
              onUpHandler={() => {
                setButtonState(BTN_X, false)
              }} 
            />
          </div>
        </div>

        <div className="gamepad-btn-zone-right">
          {/* Virtual Microphone button */}
          <div className="gamepad-btn-microphone">
            <VirtualGamepadButton 
              label={<LuMic className="gamepad-btn-icon" />} 
              onDownHandler={() => setButtonState(BTN_RB, true)} 
              onUpHandler={() => setButtonState(BTN_RB, false)} 
            />
          </div>
        </div>
        
      </div>
    </div>
  );
}

